import { Modal } from "components/Common/Modal";
import { useAuth } from "contexts/AuthContext";
import { usePortal } from "contexts/PortalContext";
import React, {
  Fragment,
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styles from "./analyticsConsentModal.module.scss";
import { ANALYTICS, ANALYTICS_CONFIG, analyticsLog } from "constants/analytics";
import { EMPTY_LIST, EMPTY_OBJECT } from "constants/defaults";
import { fpSet } from "lib/fpLodash";
import { FooterButtons } from "components/Common/FooterButtons/FooterButtons";
import {
  concat,
  entries,
  findIndex,
  get,
  isNil,
  isObject,
  keys,
  reduce,
  reverse,
  size,
} from "lodash";
import { HelpTooltip } from "components/Common/HelpTooltip";
import { getRadioValue, Radio, RADIO_STATES } from "components/Common/Radio";
import { Loader } from "components/Common/Loader";
import { getDataFromDocument } from "contexts/database/useMainAccessors";
import Highlighter from "react-highlight-words";
import classnames from "classnames/bind";
import { useAnalytics } from "contexts/AnalyticsContext";
import { TUTORIALS } from "components/Tutorial/consts/tutorialConsts";
import * as semver from "semver";
import { isMobile } from "react-device-detect";
import { POPUP_TYPES } from "./popupManagerConsts";
import { usePopupPortalManager } from "./usePopupPortalManager";

const classNameBuilder = classnames.bind(styles);

export const AnalyticsConsentModal = ({
  portalProps,
  analyticsChanges = {},
  showAll,
  allowClose,
  newestVersion,
}) => {
  const { isPortalComponentRendered, setIsLockedPortalComponentRendered } =
    portalProps;
  const { analyticsConsent } = useAnalytics();
  const [consentUpdate, setConsentUpdate] = useState(analyticsConsent);
  const [loading, setLoading] = useState(false);
  const { updateUser } = useAuth();

  useLayoutEffect(() => {
    setLoading(false);
  }, [portalProps.isPortalComponentRendered]);

  useEffect(() => {
    setConsentUpdate(analyticsConsent);
  }, [analyticsConsent, isPortalComponentRendered]);

  const visibleConfigs = showAll
    ? keys(ANALYTICS_CONFIG)
    : keys(analyticsChanges);

  return (
    <Modal
      portalProps={portalProps}
      className={styles["modal-container"]}
      contentClassName={styles["modal"]}
      closable={Boolean(allowClose)}
      onClose={() => portalProps.setIsLockedPortalComponentRendered(false)}
      title={`Analytics v${newestVersion}`}
    >
      <div className={styles["content"]}>
        {!showAll && (
          <div className={styles["description"]}>
            {analyticsLog[0].description}
          </div>
        )}
        <p style={{ textAlign: "center" }}>
          We will always notify you through this modal whenever any new events
          are tracked or old events are modified.
        </p>
        <div className={styles["important"]}>
          You can revoke consent at any time in the Settings Drawer! You can
          find it in the section labeled "Analytics"
        </div>
        {!showAll && (
          <p className={styles["legend"]}>
            <span className={styles["new"]}>
              Entirely new analytics are colored in yellow.{" "}
            </span>
            <span className={styles["updated"]}>
              Changes to existing analytics are colored in red.
            </span>
          </p>
        )}
        <div className={styles["section-label"]}>
          {showAll ? "Analytics" : "New/Updated Analytics"}
        </div>
        <div className={styles["grid"]}>
          <label>Event Type</label>
          <label>Description</label>
          <label>Why?</label>
          <label></label>
          {visibleConfigs.map((key) => {
            const { label, description, explanation } =
              ANALYTICS_CONFIG[key] || EMPTY_OBJECT;

            const entryClassName =
              analyticsChanges[key] === true ? styles["new"] : "";
            return (
              <Fragment key={key}>
                <div className={classNameBuilder(entryClassName)}>{label}</div>
                <div className={classNameBuilder(entryClassName, "pre-wrap")}>
                  <Highlighter
                    searchWords={
                      analyticsChanges[key]?.description || EMPTY_LIST
                    }
                    textToHighlight={description}
                    highlightClassName={styles["updated"]}
                    highlightTag={"span"}
                  />
                </div>
                <div className={styles["help-tooltip-container"]}>
                  <HelpTooltip
                    position="top-end"
                    iconClassName={
                      entryClassName ||
                      (analyticsChanges[key]?.explanation && styles["updated"])
                    }
                  >
                    <Highlighter
                      searchWords={
                        analyticsChanges[key]?.explanation || EMPTY_LIST
                      }
                      textToHighlight={explanation}
                      highlightClassName={styles["updated"]}
                      highlightTag={"span"}
                    />
                  </HelpTooltip>
                </div>
                <div className={styles["toggle"]}>
                  <Radio
                    className={styles["radio"]}
                    label="Disable"
                    value={
                      isNil(consentUpdate[key])
                        ? RADIO_STATES.EMPTY
                        : getRadioValue(!consentUpdate[key])
                    }
                    onClick={() =>
                      setConsentUpdate((prev) => fpSet(key, false, prev))
                    }
                  />
                  <Radio
                    className={styles["radio"]}
                    label="Allow"
                    value={
                      isNil(consentUpdate[key])
                        ? RADIO_STATES.EMPTY
                        : getRadioValue(consentUpdate[key])
                    }
                    onClick={() =>
                      setConsentUpdate((prev) => fpSet(key, true, prev))
                    }
                  />
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
      <FooterButtons
        submitText={loading ? <Loader /> : "Save Choices"}
        submitDisabled={loading || size(ANALYTICS) !== size(consentUpdate)}
        submitButtonProps={{
          colorful: !loading && size(ANALYTICS) === size(consentUpdate),
        }}
        onSubmit={async () => {
          setLoading(true);
          setIsLockedPortalComponentRendered(false);
          await updateUser((doc) => {
            const data = getDataFromDocument(doc);

            return {
              analyticsConsent: { ...data?.analyticsConsent, ...consentUpdate },
              lastAnalyticsVersion: newestVersion,
            };
          });
        }}
        onCancel={
          allowClose
            ? () => portalProps.setIsLockedPortalComponentRendered(false)
            : undefined
        }
      />
    </Modal>
  );
};

export const InitialLoadConsentModal = memo(() => {
  return;
  // const { user, updateUser } = useAuth();
  // const lastLoadedVersion = user?.lastAnalyticsVersion;
  // const changesRef = useRef();
  // const descriptionRef = useRef();
  // const isFirstAppLoad = useRef();

  // const portalProps = usePortal();
  // if (
  //   user?.id &&
  //   user?.tutorialStatuses &&
  //   user?.tutorialStatuses?.[TUTORIALS.INTRO] !== -1
  // ) {
  //   isFirstAppLoad.current = true;
  // }
  // if (
  //   isFirstAppLoad.current &&
  //   get(user?.tutorialStatuses?.[TUTORIALS.INTRO]) === -1
  // ) {
  //   isFirstAppLoad.current = false;
  // }

  // const newestVersion = analyticsLog[0].version;
  // const hasNewerVersion =
  //   !isFirstAppLoad.current &&
  //   user &&
  //   (!lastLoadedVersion || semver.lt(lastLoadedVersion, newestVersion));

  // const { checkIsUnlocked, lockPopup, unlockPopup } = usePopupPortalManager({
  //   popupType: POPUP_TYPES,
  // });

  // useEffect(() => {
  //   if (hasNewerVersion && !isMobile && checkIsUnlocked()) {
  //     lockPopup();
  //     let lastLoadedIndex = findIndex(
  //       analyticsLog,
  //       ({ version }) => version === lastLoadedVersion
  //     );
  //     const analyticsChangesArr = reverse(
  //       lastLoadedIndex === -1
  //         ? analyticsLog
  //         : analyticsLog.slice(0, lastLoadedIndex)
  //     );
  //     const analyticsChanges = reduce(
  //       analyticsChangesArr,
  //       (acc, { changes: analyticsChanges = {} }) => {
  //         Object.entries(analyticsChanges).forEach(([k, v]) => {
  //           if (!acc[k]) {
  //             acc[k] = v;
  //           } else if (acc[k] !== true && isObject(acc[k])) {
  //             entries(v).forEach(([propertyK, propertyValue]) => {
  //               // each value should be an array so just combine the arrays detailing what text is new
  //               acc[k][propertyK] = concat(
  //                 acc[k][propertyK] || [],
  //                 propertyValue
  //               );
  //             });
  //           }
  //         });

  //         return acc;
  //       },
  //       {}
  //     );
  //     changesRef.current = analyticsChanges;

  //     // WARNING: There's a bug in BaseTutorial complete() where we close the modal. In TutorialModal this is how we mark the tutorial as complete, so i don't want to risk fixing it
  //     portalProps.setIsLockedPortalComponentRendered(true);

  //     if (lastLoadedIndex === -1) {
  //       lastLoadedIndex = analyticsChanges.length - 1;
  //     }
  //   }
  // }, [
  //   hasNewerVersion,
  //   portalProps,
  //   updateUser,
  //   newestVersion,
  //   lastLoadedVersion,
  //   checkIsUnlocked,
  //   lockPopup,
  // ]);

  // useEffect(() => {
  //   if (!hasNewerVersion) {
  //     unlockPopup();
  //   }
  // }, [hasNewerVersion, unlockPopup]);

  // return (
  //   <AnalyticsConsentModal
  //     portalProps={portalProps}
  //     analyticsChanges={changesRef.current}
  //     description={descriptionRef.current}
  //     newestVersion={newestVersion}
  //   />
  // );
});
